/**
 * Application-wide constants
 * Centralized configuration values to avoid hard-coding throughout the codebase
 */

export const API_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  CACHE_DURATION: 10 * 60 * 1000, // 10 minutes
  VALIDATION_CACHE_DURATION: 10 * 60 * 1000, // 10 minutes
  REQUEST_TIMEOUT: 30000, // 30 seconds
} as const;

export const LESSON_FEES = {
  REGULAR: 170000,
  ENTRANCE_EXAM: 250000,
} as const;

export const DEFAULT_TEACHER_ID = 1;

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
} as const;

export const TIME_CONFIG = {
  DEFAULT_DURATION_HOURS: 1,
  MIN_HOUR: 0,
  MAX_HOUR: 24,
} as const;

export const PHONE_NUMBER = {
  MIN_LENGTH: 10,
  MAX_LENGTH: 11,
  DISPLAY_SEPARATOR: '-',
} as const;

export const DATE_FORMATS = {
  SHORT: 'short',
  LONG: 'long',
  KOREAN: 'ko-KR',
} as const;

export const RESPONSE_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
  PENDING: 'pending',
} as const;

export const LESSON_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CONTACTED: 'contacted',
} as const;

export const WEEKDAYS_KR = ['일', '월', '화', '수', '목', '금', '토'] as const;
export const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export type WeekdayKr = typeof WEEKDAYS_KR[number];
export type WeekdayEn = typeof WEEKDAYS_EN[number];

// Weekday mappings for consistency across the application
export const WEEKDAY_TO_NUMBER = {
  'sunday': 0,
  'monday': 1,
  'tuesday': 2,
  'wednesday': 3,
  'thursday': 4,
  'friday': 5,
  'saturday': 6
} as const;

export const WEEKDAY_KOREAN = {
  monday: '월요일',
  tuesday: '화요일',
  wednesday: '수요일',
  thursday: '목요일',
  friday: '금요일',
  saturday: '토요일',
  sunday: '일요일'
} as const;

// Status labels for different modules
export const STATUS_LABELS = {
  LESSON_REQUEST: {
    pending: '대기중',
    contacted: '연락완료',
    approved: '승인됨',
    rejected: '거절됨',
    cancelled: '취소됨'
  },
  ONEDAY_CLASS: {
    pending: '대기중',
    contacted: '연락완료',
    scheduled: '일정확정',
    completed: '수업완료',
    cancelled: '취소됨'
  },
  MODIFICATION: {
    pending: '대기중',
    approved: '승인됨',
    rejected: '거절됨',
    cancelled: '취소됨'
  },
  CONTACT: {
    pending: '대기중',
    replied: '답변완료',
    closed: '종료'
  },
  USER: {
    active: '활성',
    inactive: '비활성',
    suspended: '정지'
  }
} as const;

export const STATUS_COLORS = {
  pending: 'pending',
  contacted: 'contacted',
  approved: 'approved',
  rejected: 'rejected',
  cancelled: 'cancelled',
  scheduled: 'scheduled',
  completed: 'completed',
  replied: 'replied',
  closed: 'closed',
  active: 'active',
  inactive: 'inactive',
  suspended: 'suspended'
} as const;

// Lesson type labels
export const LESSON_TYPE_LABELS = {
  beginner: '초급',
  intermediate: '중급',
  advanced: '고급',
  kids: '키즈'
} as const;

// Weekday options for select inputs
export const WEEKDAY_OPTIONS = [
  { value: 'monday', label: '월요일' },
  { value: 'tuesday', label: '화요일' },
  { value: 'wednesday', label: '수요일' },
  { value: 'thursday', label: '목요일' },
  { value: 'friday', label: '금요일' },
  { value: 'saturday', label: '토요일' },
  { value: 'sunday', label: '일요일' }
] as const;

// Time slots for scheduling (10 AM to 10 PM - 평일 운영시간 기준, 30분 단위)
// 21:30, 22:00은 표시만 하고 선택 불가 (1시간 수업이 22시를 넘어감)
export const TIME_SLOTS = [
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
  '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'
] as const;

// Time slots for weekend (9 AM to 6 PM - 주말 운영시간 기준, 30분 단위)
export const WEEKEND_TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00'
] as const;

// Extended time slots for practice booking (11 AM to 8 PM)
export const PRACTICE_TIME_SLOTS = [
  '11:00', '12:00', '13:00', '14:00', '15:00',
  '16:00', '17:00', '18:00', '19:00', '20:00'
] as const;

// Time slots configuration
export const TIME_SLOTS_CONFIG = {
  MIN_HOUR: 11,
  MAX_HOUR: 20,
  LESSON_MIN_HOUR: 10,
  LESSON_MAX_HOUR: 22,
  PRACTICE_MIN_HOUR: 11,
  PRACTICE_MAX_HOUR: 20,
} as const;

// Date range configuration
export const DATE_RANGES = {
  TEACHER_AVAILABILITY_DAYS: 30,
  TOKEN_REFRESH_THRESHOLD_MS: 30 * 60 * 1000, // 30 minutes
  SCHEDULE_WEEKS_AHEAD: 4,
} as const;

// Schedule type labels
export const SCHEDULE_TYPE_LABELS = {
  regular: '정규수업',
  rescheduled: '변경된수업',
  cancelled: '취소됨',
  personal: '개인일정',
  oneday_class: '원데이클래스',
} as const;

export const SCHEDULE_TYPE_LABELS_EN = {
  regular: 'Regular Lesson',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
  personal: 'Personal',
  oneday_class: 'One-Day Class',
} as const;

// Inquiry category types
export const INQUIRY_CATEGORIES = {
  lesson: '레슨 문의',
  fee: '수업료 문의',
  oneday: '원데이클래스 문의',
  facility: '시설 이용 문의',
  other: '기타',
} as const;
